import {
  useState, useRef, useLayoutEffect, useEffect, useCallback, CSSProperties,
} from 'react';

export interface UseFloatingPositionOptions {
  side?: 'top' | 'bottom' | 'left' | 'right';
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
  alignOffset?: number;
  avoidCollisions?: boolean;
  collisionPadding?: number;
}

interface FloatingPosition {
  top: number;
  left: number;
  maxHeight?: number;
}

const OFFSCREEN_POSITION: FloatingPosition = { top: -9999, left: -9999 };

function arePositionsEqual(a: FloatingPosition, b: FloatingPosition): boolean {
  return (
    Math.round(a.top) === Math.round(b.top)
    && Math.round(a.left) === Math.round(b.left)
    && a.maxHeight === b.maxHeight
  );
}

/**
 * Portal-based floating position hook.
 * Calculates fixed position relative to the viewport (for portal-rendered content).
 * Handles collision detection with viewport boundaries (flip + shift).
 */
export function useFloatingPosition(
  open: boolean,
  triggerRef: React.RefObject<HTMLElement | null>,
  options: UseFloatingPositionOptions = {},
) {
  const {
    side = 'bottom',
    sideOffset = 4,
    align = 'start',
    alignOffset = 0,
    avoidCollisions = true,
    collisionPadding = 8,
  } = options;

  const contentRef = useRef<HTMLDivElement>(null);
  const lastPositionRef = useRef<FloatingPosition>(OFFSCREEN_POSITION);
  const [position, setPosition] = useState<FloatingPosition>(OFFSCREEN_POSITION);

  const commitPosition = useCallback((nextPosition: FloatingPosition) => {
    if (arePositionsEqual(lastPositionRef.current, nextPosition)) {
      return;
    }
    lastPositionRef.current = nextPosition;
    setPosition(nextPosition);
  }, []);

  const update = useCallback(() => {
    if (!open || !triggerRef.current || !contentRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;
    let resolvedSide = side;

    // ─── Flip: check if there's enough space on the preferred side ───
    if (avoidCollisions) {
      if (resolvedSide === 'bottom') {
        const spaceBelow = viewportHeight - triggerRect.bottom - collisionPadding;
        const spaceAbove = triggerRect.top - collisionPadding;
        if (contentRect.height + sideOffset > spaceBelow && spaceAbove > spaceBelow) {
          resolvedSide = 'top';
        }
      } else if (resolvedSide === 'top') {
        const spaceAbove = triggerRect.top - collisionPadding;
        const spaceBelow = viewportHeight - triggerRect.bottom - collisionPadding;
        if (contentRect.height + sideOffset > spaceAbove && spaceBelow > spaceAbove) {
          resolvedSide = 'bottom';
        }
      } else if (resolvedSide === 'right') {
        const spaceRight = viewportWidth - triggerRect.right - collisionPadding;
        const spaceLeft = triggerRect.left - collisionPadding;
        if (contentRect.width + sideOffset > spaceRight && spaceLeft > spaceRight) {
          resolvedSide = 'left';
        }
      } else if (resolvedSide === 'left') {
        const spaceLeft = triggerRect.left - collisionPadding;
        const spaceRight = viewportWidth - triggerRect.right - collisionPadding;
        if (contentRect.width + sideOffset > spaceLeft && spaceRight > spaceLeft) {
          resolvedSide = 'right';
        }
      }
    }

    // ─── Calculate position based on resolved side ───
    switch (resolvedSide) {
      case 'bottom':
        top = triggerRect.bottom + sideOffset;
        break;
      case 'top':
        top = triggerRect.top - contentRect.height - sideOffset;
        break;
      case 'right':
        left = triggerRect.right + sideOffset;
        break;
      case 'left':
        left = triggerRect.left - contentRect.width - sideOffset;
        break;
    }

    // ─── Calculate alignment (cross-axis) ───
    if (resolvedSide === 'bottom' || resolvedSide === 'top') {
      switch (align) {
        case 'start':
          left = triggerRect.left + alignOffset;
          break;
        case 'center':
          left = triggerRect.left + triggerRect.width / 2 - contentRect.width / 2 + alignOffset;
          break;
        case 'end':
          left = triggerRect.right - contentRect.width - alignOffset;
          break;
      }
    } else {
      // side is left or right
      switch (align) {
        case 'start':
          top = triggerRect.top + alignOffset;
          break;
        case 'center':
          top = triggerRect.top + triggerRect.height / 2 - contentRect.height / 2 + alignOffset;
          break;
        case 'end':
          top = triggerRect.bottom - contentRect.height - alignOffset;
          break;
      }
    }

    // ─── Shift: keep within viewport bounds ───
    if (avoidCollisions) {
      // Horizontal shift
      if (left + contentRect.width > viewportWidth - collisionPadding) {
        left = viewportWidth - contentRect.width - collisionPadding;
      }
      if (left < collisionPadding) {
        left = collisionPadding;
      }
      // Vertical shift
      if (top + contentRect.height > viewportHeight - collisionPadding) {
        top = viewportHeight - contentRect.height - collisionPadding;
      }
      if (top < collisionPadding) {
        top = collisionPadding;
      }
    }

    commitPosition({ top, left });
  }, [open, triggerRef, side, sideOffset, align, alignOffset, avoidCollisions, collisionPadding, commitPosition]);

  // Initial position calculation
  useLayoutEffect(() => {
    update();
  }, [update]);

  // Auto-update on scroll and resize
  useEffect(() => {
    if (!open) return undefined;

    const handleUpdate = () => {
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', handleUpdate, true); // capture phase for nested scrolls
    window.addEventListener('resize', handleUpdate);

    // ResizeObserver for content size changes
    let resizeObserver: ResizeObserver | undefined;
    if (contentRef.current) {
      resizeObserver = new ResizeObserver(handleUpdate);
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      resizeObserver?.disconnect();
    };
  }, [open, update]);

  useEffect(() => {
    if (!open) {
      commitPosition(OFFSCREEN_POSITION);
      return undefined;
    }

    let frame = 0;
    const tick = () => {
      update();
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [open, update, commitPosition]);

  const floatingStyle: CSSProperties = {
    position: 'fixed',
    top: position.top,
    left: position.left,
    zIndex: 9999,
  };

  return { contentRef, floatingStyle, update };
}

export default useFloatingPosition;
